import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PIService } from '../services/api';
import { PrincipalInvestigator } from '../types';
import { useDebounce } from '../hooks/useDebounce';
import RatingGrid from '../components/RatingGrid';

const Home: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PrincipalInvestigator[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    const fetchResults = async () => {
      if (debouncedSearchQuery) {
        setIsSearching(true);
        try {
          const results = await PIService.search(debouncedSearchQuery);
          setSearchResults(results);
        } catch (error) {
          console.error('Error searching PIs:', error);
        }
        setIsSearching(false);
      } else {
        setSearchResults([]);
      }
    };

    fetchResults();
  }, [debouncedSearchQuery]);

  const handleDemoRatingChange = () => {}; // Empty function for demo

  return (
    <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
      {/* Hero Section with Search */}
      <div className="mb-16 text-center">
        <h1 className="mb-4 text-4xl font-bold text-gray-900">
          Rate Your PI
        </h1>
        <p className="mb-8 text-xl text-gray-600">
          Help others scientists make informed decisions about potential labs and advisors
        </p>
        <div className="max-w-3xl mx-auto">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for a PI by name or institution..."
              className="w-full px-6 py-4 text-lg transition-colors border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
            />
            {isSearching && (
              <div className="absolute transform -translate-y-1/2 right-4 top-1/2">
                <div className="w-6 h-6 border-b-2 rounded-full animate-spin border-primary-500"></div>
              </div>
            )}
          </div>
          
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-lg">
              {searchResults.map((pi) => (
                <Link
                  key={pi.id}
                  to={`/pi/${pi.id}`}
                  className="block px-6 py-4 transition-colors border-b hover:bg-gray-50 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{pi.name}</h3>
                      <p className="text-sm text-gray-600">{pi.institution_name}</p>
                    </div>
                    <div className="text-sm text-gray-500">
                      {pi.research_field}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* How it Works Section */}
      <div className="mb-16">
        <h2 className="mb-8 text-3xl font-bold text-center text-gray-900">How It Works</h2>
        <div className="grid gap-8 md:grid-cols-3">
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100">
              <span className="text-2xl">1</span>
            </div>
            <h3 className="mb-2 text-xl font-semibold">Verify & Generate an Anonymous ID</h3>
            <p className="text-gray-600">
              Verify with your academic email to generate an anonymous ID. Your email is immediately discarded and never stored.
            </p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100">
              <span className="text-2xl">2</span>
            </div>
            <h3 className="mb-2 text-xl font-semibold">Rate a PI</h3>
            <p className="text-gray-600">
              Your anonymous ID can only rate PIs at your institution, and can only rate up to 3 PIs in total.
            </p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100">
              <span className="text-2xl">3</span>
            </div>
            <h3 className="mb-2 text-xl font-semibold">Complete Privacy</h3>
            <p className="text-gray-600">
              Your ratings are completely anonymous. We never store emails and there's no identifying information linked to these IDs other than their institution.
            </p>
          </div>
        </div>
        <div className="p-6 mt-8 text-center rounded-lg bg-primary-50">
          <p className="text-gray-700">
            <strong>Your Privacy is Our Priority:</strong> When you verify with your academic email, we generate a random anonymous ID, link it to that institution, and then immediately discard your email address - it's never saved. Whilst this might be annoying, limiting reviews to only verified emails for a given institution is the best tradeoff to ensure anonymity whilst limiting spam reviews.
          </p>
        </div>
      </div>

      {/* Demo Rating Grid */}
      <div className="mb-16">
        <h2 className="mb-8 text-3xl font-bold text-center text-gray-900">Understanding Our Rating System</h2>
        <div className="max-w-4xl mx-auto">
          <RatingGrid
            onRatingChange={() => {}}
            initialLabManagementScore={25}
            initialInterpersonalScore={30}
            readonly={true}
          />
          <div className="mt-8 text-center text-gray-600">
            <p>Rate PIs on two key dimensions to generate their Egg Score:</p>
            <ul className="mt-4 space-y-2">
              <li><strong>Lab Management:</strong> From toxic to healthy work environment</li>
              <li><strong>Interpersonal Behavior:</strong> From negligent/harmful to supportive mentorship</li>
            </ul>
            <p className="mt-4 text-sm">
              The Egg Score is calculated based on the distance from the ideal point (100,100),
              where a score of 100 means perfect lab management and interpersonal behavior.
            </p>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <section className="py-12">
        <h2 className="mb-8 text-3xl font-bold text-center text-neutral-800">
          Why Use Egg Score?
        </h2>
        
        <div className="grid gap-8 md:grid-cols-2">
          <div className="p-6 bg-white border shadow-sm rounded-xl border-neutral-200">
            <h3 className="mb-3 text-xl font-semibold">Completely Anonymous</h3>
            <p className="text-neutral-600">
              Your email is verified then discarded. Ratings cannot be traced back to you.
            </p>
          </div>
          
          <div className="p-6 bg-white border shadow-sm rounded-xl border-neutral-200">
            <h3 className="mb-3 text-xl font-semibold">Institution Verified</h3>
            <p className="text-neutral-600">
              Rate PIs at your institution for accurate, firsthand experiences.
            </p>
          </div>
          
          <div className="p-6 bg-white border shadow-sm rounded-xl border-neutral-200">
            <h3 className="mb-3 text-xl font-semibold">Comprehensive Rating System</h3>
            <p className="text-neutral-600">
              Evaluate both interpersonal behavior and lab management in one intuitive grid.
            </p>
          </div>
          
          <div className="p-6 bg-white border shadow-sm rounded-xl border-neutral-200">
            <h3 className="mb-3 text-xl font-semibold">Make Informed Decisions</h3>
            <p className="text-neutral-600">
              Know what you're getting into before joining a lab. Avoid toxic environments.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 